import { inject, injectable } from 'inversify';
import type { SideNavItem } from '@design-system/side-nav/application/component/SideNav/SideNav.types';
import { MainLayoutDependencyTypes } from '@client/composition/main-layout/main-layout.dependency-types';
import type { MainLayoutService } from '@client/composition/main-layout/domain/service/main-layout.service';
import type { CurrentClientUserClient } from '@client/entity/current-client-user/domain/model/current-client-user-client.model';
import type { CurrentClientUserSelectedClientService } from '@client/entity/current-client-user/domain/service/current-client-user-selected-client.service';
import { CurrentClientUserDependencyTypes } from '@client/entity/current-client-user/current-client-user.dependency-types';
import type { CurrentClientUserStoreQuery } from '@client/entity/current-client-user/domain/store/current-client-user.store-query';
import type { CurrentClientUserStoreCommand } from '@client/entity/current-client-user/domain/store/current-client-user.store-command';
import { GameDetailRouteName } from '@client/feature/game-detail/domain/route/game-detail.route-name';
import { EventDependencyTypes, EventManager, type ListenerInterface } from '@kiplin/web-common/event';
import { CurrentClientChangedEvent } from '@client/entity/current-client-user/domain/event/current-client-changed.event';
import { NetworkDependencyTypes } from '@shared/common/network/network.dependency-types';
import type { JwtRepositoryInterface } from '@shared/common/network/domain/repository/jwt.repository';
import { LoginRouteName } from '@shared/feature/login/application/routes/login.route-name';
import { CustomerChatbotDependencyTypes } from '@client/feature/customer-chatbots/customer-chatbot.dependency-types';
import type { CustomerChatbotService } from '@client/feature/customer-chatbots/domain/service/customer-chatbot.service';
import { container } from '@shared/common/dependency-injection/domain/container';
import { AppModuleLoader, DIDependencyTypes } from '@kiplin/web-common/dependency-injection';
import type { TrackingService } from '@shared/common/tracking/domain/service/tracking.service';
import { TrackingDependencyTypes } from '@shared/common/tracking/tracking.dependency-types';
import { TrackingEventNames } from '@client/common/tracking/domain/constant/tracking-event-names.constant';
import { HomeRouteName } from '@shared/feature/home/domain/route/home.route-name';
import type { GameCreationModalOpenerService } from '@client/feature/game-creation/domain/service/game-creation-modal-opener.service';
import { FeatureFlagKey } from '@client/common/feature-flags/domain/constant/feature-flag-key.constant';
import { NavigationDependencyTypes, type RouterInterface } from '@kiplin/web-common/navigation';
import type { MainLayoutSubscriptionSubscriberProgressService } from '@client/composition/main-layout/domain/service/main-layout-subscription-subscriber-progress.service';
import type { MainLayoutStoreQuery } from '@client/composition/main-layout/domain/store/main-layout.store-query';
import type { MainLayoutStoreCommand } from '@client/composition/main-layout/domain/store/main-layout.store-command';
import type { CurrentClientUserSelectionService } from '@client/entity/current-client-user/domain/service/current-client-user-selection.service';
import { TrackedUserTypes } from '@shared/common/tracking/domain/constant/tracked-user-types.constant';

@injectable()
export class MainLayoutPresenter {
  private hasInitialized = false;

  public constructor(
    @inject(MainLayoutDependencyTypes.MainLayoutService)
    private readonly mainLayoutService: MainLayoutService,
    @inject(CurrentClientUserDependencyTypes.CurrentClientUserSelectedClientService)
    private readonly selectedClientService: CurrentClientUserSelectedClientService,
    @inject(CurrentClientUserDependencyTypes.CurrentClientUserStoreQuery)
    private readonly currentClientUserStoreQuery: CurrentClientUserStoreQuery,
    @inject(CurrentClientUserDependencyTypes.CurrentClientUserStoreCommand)
    private readonly currentClientUserStoreCommand: CurrentClientUserStoreCommand,
    @inject(CurrentClientUserDependencyTypes.CurrentClientUserSelectionService)
    private readonly currentClientUserSelectionService: CurrentClientUserSelectionService,
    @inject(MainLayoutDependencyTypes.MainLayoutStoreQuery)
    private readonly mainLayoutStoreQuery: MainLayoutStoreQuery,
    @inject(MainLayoutDependencyTypes.MainLayoutStoreCommand)
    private readonly mainLayoutStoreCommand: MainLayoutStoreCommand,
    @inject(EventDependencyTypes.EventManager)
    private readonly eventManager: EventManager,
    @inject(CustomerChatbotDependencyTypes.CustomerChatbotService)
    private readonly customerChatbotService: CustomerChatbotService,
    @inject(NetworkDependencyTypes.JwtRepository)
    private readonly jwtRepository: JwtRepositoryInterface,
    @inject(TrackingDependencyTypes.TrackingService)
    private readonly trackingService: TrackingService,
    @inject(NavigationDependencyTypes.Router)
    private readonly router: RouterInterface,
    @inject(MainLayoutDependencyTypes.MainLayoutSubscriptionSubscriberProgressService)
    private readonly subscriptionSubscriberProgressService: MainLayoutSubscriptionSubscriberProgressService,
    @inject(MainLayoutDependencyTypes.MainLayoutClientGamesRefreshRequestedListener)
    private readonly mainLayoutClientGamesRefreshRequestedListener: ListenerInterface,
    @inject(MainLayoutDependencyTypes.MainLayoutGameCreationModalLifecycleListener)
    private readonly mainLayoutGameCreationModalLifecycleListener: ListenerInterface,
  ) {}

  public initialize(): void {
    if (this.hasInitialized) {
      return;
    }

    this.hasInitialized = true;
    this.currentClientUserSelectionService.ensureSelectedClient();
    this.showUserInfos();
    this.loadNavigationItems();
    void this.subscriptionSubscriberProgressService.loadSubscriptionSubscriberProgress(
      this.selectedClientService.getSelectedClient(),
    );
    this.identifyCurrentClientUser();
    this.mainLayoutClientGamesRefreshRequestedListener.activate();
    this.mainLayoutGameCreationModalLifecycleListener.activate();
  }

  public loadNavigationItems(): void {
    const selectedClient = this.selectedClientService.getSelectedClient();

    this.mainLayoutStoreCommand.updateMainLayoutUiModel({
      navigationElements: this.mainLayoutService.buildGamesNavigationSections(selectedClient),
    });
  }

  public showUserInfos(): void {
    const definedCurrentClientUser = this.currentClientUserStoreQuery.getDefinedCurrentClientUser();
    const selectedClient = this.selectedClientService.getSelectedClient();
    if (selectedClient) {
      this.currentClientUserStoreCommand.setSelectedClient(selectedClient);
    }

    this.mainLayoutStoreCommand.updateMainLayoutUiModel({
      userInfo: {
        fullName: this.buildProfileName(
          definedCurrentClientUser.firstname,
          definedCurrentClientUser.lastname,
          definedCurrentClientUser.email,
        ),
        email: definedCurrentClientUser.email,
        companyLogoUrl: selectedClient?.logo ?? null,
        companyLabel: selectedClient?.name ?? '',
        selectOptions:
          definedCurrentClientUser.clients?.map((client) => ({
            label: client.name,
            value: client.id,
            logoUrl: client.logo,
          })) ?? [],
        selectValue: selectedClient?.id,
      },
      userInfos: {
        firstname: definedCurrentClientUser.firstname,
        lastname: definedCurrentClientUser.lastname,
        email: definedCurrentClientUser.email,
        avatarImgUrl: selectedClient?.logo ?? null,
        clients:
          definedCurrentClientUser.clients?.map((client) => ({
            label: client.name,
            value: client.id,
          })) ?? [],
      },
      clientInfos: {
        subscriptionEndDate: selectedClient?.subscriptionEndDate ?? null,
        subscriptionStartDate: selectedClient?.subscriptionStartDate ?? null,
        subscriptionCode: this.getSubscriptionCode(selectedClient),
        label: selectedClient?.name ?? '',
        id: selectedClient?.id ?? '',
      },
      isCreateNewGameButtonShown: this.canShowCreateNewGameButton(selectedClient),
    });
  }

  public async changeSelectedClient(clientId: string): Promise<void> {
    if (!this.currentClientUserStoreQuery.getCurrentClientUser()) {
      return;
    }
    const selectedClient = this.selectedClientService.getSelectedClient(clientId);
    if (!selectedClient) {
      return;
    }
    this.trackingService.track(TrackingEventNames.ClientChanged, { client_id: clientId });
    this.currentClientUserSelectionService.selectClient(selectedClient);

    const mainLayoutUiModel = this.mainLayoutStoreQuery.getMainLayoutUiModel();
    this.mainLayoutStoreCommand.updateMainLayoutUiModel({
      navigationElements: this.mainLayoutService.buildGamesNavigationSections(selectedClient),
      userInfo: {
        ...mainLayoutUiModel.userInfo,
        companyLogoUrl: selectedClient.logo ?? null,
        companyLabel: selectedClient.name,
        selectValue: selectedClient.id,
      },
      clientInfos: {
        id: selectedClient.id,
        label: selectedClient.name,
        subscriptionCode: this.getSubscriptionCode(selectedClient),
        subscriptionEndDate: selectedClient.subscriptionEndDate,
        subscriptionStartDate: selectedClient.subscriptionStartDate,
      },
      isCreateNewGameButtonShown: this.canShowCreateNewGameButton(selectedClient),
      userInfos: {
        ...mainLayoutUiModel.userInfos,
      },
    });

    this.eventManager.dispatch(
      new CurrentClientChangedEvent({
        detail: { clientId: selectedClient.id },
      }),
    );
    void this.subscriptionSubscriberProgressService.loadSubscriptionSubscriberProgress(selectedClient);
    await this.handlePageRedirectionAfterSelectedClientChange();
  }

  private async handlePageRedirectionAfterSelectedClientChange(): Promise<void> {
    await this.router.push({ name: HomeRouteName.Home });
  }

  private getSubscriptionCode(client: CurrentClientUserClient | null): string | null {
    if (!client?.hasActiveSubscription || !client.accessCode) {
      return null;
    }

    return client.accessCode;
  }

  private canShowCreateNewGameButton(client: CurrentClientUserClient | null): boolean {
    return client?.hasActiveSubscription === true && this.trackingService.isFeatureEnabled(FeatureFlagKey.GameCreation);
  }

  private buildProfileName(
    firstname: string | null | undefined,
    lastname: string | null | undefined,
    email: string,
  ): string {
    const name = `${firstname ?? ''} ${lastname ?? ''}`.trim();
    return name.length > 0 ? name : email;
  }

  public async navigate(navigationItem: SideNavItem): Promise<void> {
    if (!navigationItem.associatedRouteName) {
      return;
    }

    await this.router.push({
      name: navigationItem.associatedRouteName,
      params: navigationItem.associatedRouteParams,
    });
  }

  public updateActiveNavigation(routeName: string | null, routeParams: Record<string, unknown> = {}): void {
    const routeGameId = routeParams.gameId;
    const isGameRoute =
      routeName === GameDetailRouteName.Detail ||
      routeName === GameDetailRouteName.Quests ||
      routeName === GameDetailRouteName.Entities ||
      routeName === GameDetailRouteName.PlayersList ||
      routeName === GameDetailRouteName.Statistics;
    const activeItemId = isGameRoute && typeof routeGameId === 'string' ? routeGameId : routeName;

    this.mainLayoutStoreCommand.updateMainLayoutUiModel({
      activeItemNavigationElementId: activeItemId,
    });
  }

  public async logout(): Promise<void> {
    this.trackingService.track(TrackingEventNames.UserLoggedOut);
    this.trackingService.clearIdentity();
    this.customerChatbotService.reset();
    this.jwtRepository.clearToken();
    this.currentClientUserStoreCommand.reset();
    this.mainLayoutStoreCommand.reset();
    await this.router.push({ name: LoginRouteName.Login });
  }

  public trackAccessCodeCopied(): void {
    this.trackingService.track(TrackingEventNames.AccessCodeCopied);
  }

  private identifyCurrentClientUser(): void {
    const user = this.currentClientUserStoreQuery.getCurrentClientUser();
    if (user) {
      this.trackingService.identify(user.id, TrackedUserTypes.CockpitClientUser);
    }
  }

  public async openCustomisationAppModal(): Promise<void> {
    const { CustomisationAppDependencyTypes } =
      await import('@client/feature/customisation-app/customisation-app.dependency-types');

    const { default: CustomisationAppAppModule } =
      await import('@client/feature/customisation-app/customisation-app.app-module');

    const appModuleLoader = container.get<AppModuleLoader>(DIDependencyTypes.AppModuleLoader);
    await appModuleLoader.loadIfNotAlreadyLoaded(CustomisationAppAppModule, false);

    this.mainLayoutStoreCommand.setIsCustomisationAppModalUiReady(true);

    const customisationAppModalOpenerService = container.get(
      CustomisationAppDependencyTypes.CustomisationAppModalOpenerService,
    ) as { open(): Promise<void> };

    await customisationAppModalOpenerService.open();
  }

  public async openCreateGameModal(): Promise<void> {
    const { GameCreationDependencyTypes } =
      await import('@client/feature/game-creation/game-creation.dependency-types');

    const appModuleLoader = container.get<AppModuleLoader>(DIDependencyTypes.AppModuleLoader);
    const { GameCreationAppModule } = await import('@client/feature/game-creation/game-creation.app-module');
    await appModuleLoader.loadIfNotAlreadyLoaded(GameCreationAppModule, false);

    const createGameModalOpenerService = container.get<GameCreationModalOpenerService>(
      GameCreationDependencyTypes.GameCreationModalOpenerService,
    );

    await createGameModalOpenerService.open();
  }
}
